import React, { useContext } from "react";
import Button from "react-bootstrap/Button";
import Modal from "react-bootstrap/Modal";
import Form from "react-bootstrap/Form";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { getAccessToken, getCurrentLocale } from "../Utils/Utils.js";
import DeleteIcon from "@mui/icons-material/Delete";
import { AccountContext } from "../../context/AccountContext";
import "../Utils/Utils.css";

// Helper function for creating/updating patient.
// Resolves with the saved patient's id so the caller can refresh its own state;
// this used to end in `window.location.href`, which reloaded the whole app on
// every save.
function createPatient(data, status, account_patient_id, clientSet) {
  let action = status === "n" ? "create" : "update";
  const currentLocale = getCurrentLocale();
  const url =
    process.env.REACT_APP_SOPHYAPP_API_URL +
    `/${currentLocale}/react/` +
    getAccessToken() +
    "/patient/" +
    action;
  var params = new FormData();
  params.append("account_patient[name]", data.firstname + " " + data.lastname);
  params.append("account_patient[firstname]", data.firstname);
  params.append("account_patient[lastname]", data.lastname);
  params.append("account_patient[email]", data.mail);
  params.append("account_patient[diagnostic]", data.diagnostic);
  params.append("account_patient[sexuality]", data.sexuality);
  params.append("account_patient[archived]", data.archived === true ? 1 : 0);
  if (clientSet) {
    params.append("account_patient[location]", data.location);
  }
  if (action === "update")
    params.append("account_patient[account_patient_id]", account_patient_id);
  return axios
    .post(url, params)
    .then((response) => response.data.id);
}

// Helper function for removing a patient
const handleRemoveAction = (event, patientId, accountId, onHide, t, onDeleted) => {
  event.preventDefault();
  const confirmed = window.confirm(t("admin.patient.deleteConfirm"));
  if (!confirmed) {
    return;
  }
  const currentLocale = getCurrentLocale();
  const url =
    process.env.REACT_APP_SOPHYAPP_API_URL +
    `/${currentLocale}/react/${getAccessToken()}/patient/delete_patient`;
  var params = new FormData();
  params.append("account_patient_id", patientId);
  params.append("account_id", accountId);
  axios
    .post(url, params)
    .then(function () {
      // The list owner drops the row and reports it; no full reload.
      if (onDeleted) {
        onDeleted(patientId);
      } else {
        window.location.reload();
      }
    })
    .catch(function (error) {
      alert(`${t("admin.patient.errorPrefix")}${error.message}`);
    });
  onHide();
};

function PatientModal(props) {
  const { t } = useTranslation();
  const { userData } = useContext(AccountContext);
  const [isSaving, setIsSaving] = React.useState(false);
  // The callbacks are ours, not Modal's — spreading them would put them on the
  // DOM node underneath.
  const { onSaved, onDeleted, ...modalProps } = props;
  let clientSet = userData && userData.client_id > 0 ? true : false;
  let removeAction = null;
  let modal_title = t("patients.new-patient");
  if (props.status === "n") {
    modal_title = t("patients.new-patient");
  } else {
    modal_title = t("patients.edit-patient");
    removeAction = (
      <Button
        type="danger"
        className="delete-button"
        onClick={(e) =>
          handleRemoveAction(
            e,
            props.patient.id,
            props.data.account_id,
            props.onHide,
            t,
            onDeleted
          )
        }
      >
        <DeleteIcon />
      </Button>
    );
  }

  const handleSubmit = (event) => {
    event.preventDefault();
    event.stopPropagation();
    const form = event.currentTarget;

    if (form.checkValidity() === false || isSaving) {
      return;
    }

    setIsSaving(true);
    createPatient(
      props.patientData,
      props.status,
      props.patient?.id || null,
      clientSet
    )
      .then((savedId) => {
        // Hand the id back so the caller can refresh its own state. Without a
        // handler we keep the old hard navigation rather than leaving stale
        // data on screen.
        if (onSaved) {
          onSaved(savedId, props.status);
          props.onHide();
        } else {
          window.location.href = `/patients/${savedId}`;
        }
      })
      .catch((error) => {
        if (error.response && error.response.status === 403) {
          alert(t("admin.patient.noPermission"));
        } else {
          alert(t("admin.patient.saveError"));
        }
      })
      .finally(() => setIsSaving(false));
  };

  return (
    <Modal
      {...modalProps}
      className="patient-modal"
      size="lg"
      aria-labelledby="contained-modal-title-vcenter"
      centered
    >
      <Form onSubmit={handleSubmit}>
        <Modal.Header closeButton>
          <div className="w-100">
            <div className="d-flex align-items-center justify-content-between">
              <Modal.Title id="contained-modal-title-vcenter">
                {modal_title}
              </Modal.Title>
              <div className="d-flex align-items-center gap-3 me-3">
                <Form.Group
                  controlId="formBasicCheckbox"
                  className="mb-0 d-flex align-items-center"
                >
                  <Form.Check
                    type="checkbox"
                    label={t("patients.inactive")}
                    checked={props.patientData.archived}
                    name="archived"
                    className="me-0"
                    onChange={(e) => {
                      props.handleOnChangeModal(e.target.name, e.target.checked);
                    }}
                  />
                </Form.Group>
                {removeAction}
              </div>
            </div>
          </div>
        </Modal.Header>

        <Modal.Body>
          <Form.Group className="mb-3" controlId="exampleForm.ControlInput1">
            <Form.Group className="mb-3" controlId="exampleForm.ControlInput1">
              <Form.Control value={1} type="hidden" name="id" />
            </Form.Group>
            <Form.Label>{t("profile.title")}</Form.Label>
            <Form.Control
              as="select"
              name="sexuality"
              value={props.patientData.sexuality}
              onChange={(e) => {
                props.handleOnChangeModal(e.target.name, e.target.value);
              }}
            >
              <option value="male">{t("profile.man")}</option>
              <option value="female">{t("profile.woman")}</option>
              <option value="NULL">{t("profile.noGender")}</option>
            </Form.Control>
          </Form.Group>
          <Form.Group className="mb-3" controlId="exampleForm.ControlInput1">
            <Form.Label>{t("profile.firstname")}</Form.Label>
            <Form.Control
              placeholder={t("admin.patient.firstName")}
              name="firstname"
              value={props.patientData.firstname}
              autoFocus
              required
              onChange={(e) =>
                props.handleOnChangeModal(e.target.name, e.target.value)
              }
            />
          </Form.Group>
          <Form.Group className="mb-3" controlId="exampleForm.ControlInput1">
            <Form.Label>{t("profile.lastname")}</Form.Label>
            <Form.Control
              placeholder={t("admin.patient.lastName")}
              name="lastname"
              value={props.patientData.lastname}
              required
              onChange={(e) =>
                props.handleOnChangeModal(e.target.name, e.target.value)
              }
            />
          </Form.Group>
          <Form.Group className="mb-3" controlId="exampleForm.ControlInput1">
            <Form.Label>{t("profile.email")}</Form.Label>
            <Form.Control
              type="email"
              name="mail"
              placeholder="user@patient.com"
              value={props.patientData.mail}
              pattern="[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,4}$"
              onChange={(e) =>
                props.handleOnChangeModal(e.target.name, e.target.value)
              }
            />
          </Form.Group>
          <Form.Group className="mb-3" controlId="exampleForm.ControlInput1">
            <Form.Label>{t("profile.diagnosis")}</Form.Label>
            <Form.Control
              name="diagnostic"
              placeholder="Arterielle Hypertonie"
              value={props.patientData.diagnostic}
              onChange={(e) =>
                props.handleOnChangeModal(e.target.name, e.target.value)
              }
            />
          </Form.Group>
          {clientSet && (
            <Form.Group className="mb-3" controlId="exampleForm.ControlInput1">
              <Form.Label>{t("general.location")}</Form.Label>
              <Form.Control
                as="select"
                name="location"
                value={props.patientData.location}
                onChange={(e) =>
                  props.handleOnChangeModal(e.target.name, e.target.value)
                }
              >
                <option value="">Kein Standort</option>
                {props.locations.map((location) => (
                  <option key={location.id} value={location.id}>
                    {location.name}
                  </option>
                ))}
              </Form.Control>
            </Form.Group>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button className="bs-button-color" type="submit" disabled={isSaving}>
            {t("profile.save")}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}

export default PatientModal;
